import {
  FOLDER_STRUCTURES,
  FOLDER_STRUCTURE_PATTERNS,
  S3_IMAGE_SIZE,
  VALIDATION_MESSAGES,
} from "constants/S3Constant";
import Utils from "utils";

export const validateFile = (file) => {
  const errors = [];

  if (!file || !file.type) {
    errors.push("Invalid file object");
    return { isValid: false, errors };
  }

  if (!file.type.startsWith("image/")) {
    errors.push(VALIDATION_MESSAGES.NOT_IMAGE);
  }

  if (file.size / 1024 / 1024 > S3_IMAGE_SIZE) {
    errors.push(VALIDATION_MESSAGES.FILE_TOO_LARGE);
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

export const detectFolderStructure = (files) => {
  const structureTypes = new Set();
  const validFiles = [];
  const invalidFiles = [];

  files.forEach((fileObj) => {
    try {
      const pathParts = fileObj.relativePath
        ? fileObj.relativePath.split("/")
        : [];

      if (!pathParts || pathParts.length === 0) {
        invalidFiles.push(fileObj);
        return;
      }

      // Filter out empty parts
      const cleanPathParts = pathParts.filter((part) => part && part.trim());

      if (cleanPathParts.length === 0) {
        invalidFiles.push(fileObj);
        return;
      }

      // Check which structure this file matches
      if (
        FOLDER_STRUCTURE_PATTERNS[
          FOLDER_STRUCTURES.DIVISION_STUDENT_POSES
        ].validation(cleanPathParts)
      ) {
        structureTypes.add(FOLDER_STRUCTURES.DIVISION_STUDENT_POSES);
        validFiles.push({
          ...fileObj,
          structureType: FOLDER_STRUCTURES.DIVISION_STUDENT_POSES,
          pathParts: cleanPathParts,
        });
      } else if (
        FOLDER_STRUCTURE_PATTERNS[FOLDER_STRUCTURES.STUDENT_POSES].validation(
          cleanPathParts
        )
      ) {
        structureTypes.add(FOLDER_STRUCTURES.STUDENT_POSES);
        validFiles.push({
          ...fileObj,
          structureType: FOLDER_STRUCTURES.STUDENT_POSES,
          pathParts: cleanPathParts,
        });
      } else {
        invalidFiles.push(fileObj);
      }
    } catch (error) {
      console.error("Error processing file:", fileObj.name, error);
      invalidFiles.push(fileObj);
    }
  });

  // Check for mixed structures
  if (structureTypes.size > 1) {
    return {
      isValid: false,
      error: VALIDATION_MESSAGES.MIXED_STRUCTURES,
      detectedStructures: Array.from(structureTypes),
      validFiles: [],
      invalidFiles: files,
    };
  }

  if (structureTypes.size === 0) {
    return {
      isValid: false,
      error: VALIDATION_MESSAGES.INVALID_STRUCTURE,
      detectedStructures: [],
      validFiles: [],
      invalidFiles: files,
    };
  }

  return {
    isValid: true,
    detectedStructure: Array.from(structureTypes)[0],
    validFiles,
    invalidFiles,
  };
};

export const generateStudentMapping = (files, structureType) => {
  const studentMapping = {};

  try {
    files.forEach((fileObj) => {
      const pathParts =
        fileObj.pathParts ||
        (fileObj.relativePath ? fileObj.relativePath.split("/") : []);

      if (!pathParts || pathParts.length === 0) {
        console.warn("Invalid path parts for file:", fileObj.name);
        return;
      }

      let studentId, divisionId, fileName;

      try {
        if (structureType === FOLDER_STRUCTURES.DIVISION_STUDENT_POSES) {
          if (pathParts.length < 3) {
            console.warn(
              "Invalid path depth for division/student/poses structure:",
              pathParts
            );
            return;
          }
          divisionId = pathParts[0];
          studentId = pathParts[1];
          fileName = pathParts[pathParts.length - 1];
        } else if (structureType === FOLDER_STRUCTURES.STUDENT_POSES) {
          if (pathParts.length < 2) {
            console.warn(
              "Invalid path depth for student/poses structure:",
              pathParts
            );
            return;
          }
          studentId = pathParts[0];
          fileName = pathParts[pathParts.length - 1];
        } else {
          console.warn("Unknown structure type:", structureType);
          return;
        }

        if (!studentId || !fileName) {
          console.warn("Missing studentId or fileName:", {
            studentId,
            fileName,
            pathParts,
          });
          return;
        }

        const fileNameWithoutExt = fileName.split(".")[0];
        // Apply formatTextClean to the filename before storing
        const cleanFileName = Utils.formatTextClean(fileNameWithoutExt, "-");

        if (!studentMapping[studentId]) {
          studentMapping[studentId] = {
            filenames: [],
            files: [],
            divisionId: divisionId || null,
          };
        }

        // Use the cleaned filename for API data
        if (!studentMapping[studentId].filenames.includes(cleanFileName)) {
          studentMapping[studentId].filenames.push(cleanFileName);
          studentMapping[studentId].files.push(fileObj);
        }
      } catch (error) {
        console.error("Error processing file path:", fileObj.name, error);
      }
    });

    return studentMapping;
  } catch (error) {
    console.error("Error generating student mapping:", error);
    return {};
  }
};
